import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  onCategories,
  onCategoriesAdd,
  onCategoriesDelete,
  onCategoriesEdit,
} from "../../redux/homeAction";
import CategorySelector from "./CategorySelector";

const Categories = () => {
  const dispatch = useDispatch();

  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    parent: "",
    image: null,
  });

  const [preview, setPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    dispatch(onCategories(setLoading, setCategories));
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "images") {
      const file = files[0];
      setFormData((p) => ({ ...p, image: file }));
      setPreview(URL.createObjectURL(file));
      return;
    }

    if (name === "name") {
      setFormData((p) => ({
        ...p,
        name: value,
        slug: value.toLowerCase().replace(/\s+/g, "-"),
      }));
      return;
    }

    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const data = new FormData();
    data.append("name", formData.name);
    data.append("slug", formData.slug);
    if (formData.parent) data.append("parent", formData.parent);
    if (formData.image) data.append("images", formData.image);

    if (editingId) {
      dispatch(onCategoriesEdit(setLoading, data, editingId, setShowModal));
    } else {
      dispatch(onCategoriesAdd(setLoading, data, setShowModal));
    }

    setFormData({ name: "", slug: "", parent: "", image: null });
    setPreview(null);
    setEditingId(null);
  };

  const handleEdit = (cat) => {
    setFormData({
      name: cat.name,
      slug: cat.slug,
      parent: cat.parent?._id || "",
      image: null,
    });
    setPreview(cat.images);
    setEditingId(cat._id);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this category?")) {
      dispatch(onCategoriesDelete(setLoading, id));
    }
  };

  const handleCategoryChange = useCallback((id) => {
    setFormData((p) => ({ ...p, parent: id }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Categories Management
          </h1>
          <p className="text-gray-500 text-sm">
            Manage product categories and hierarchy
          </p>
        </div>

        <button
          onClick={() => {
            setShowModal(true);
            setEditingId(null);
            setPreview(null);
            setFormData({ name: "", slug: "", parent: "", image: null });
          }}
          className="mt-4 md:mt-0 bg-black text-white px-5 py-2 rounded-lg hover:bg-gray-800 transition"
        >
          + Add Category
        </button>
      </div>

      {/* GRID */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {categories?.length > 0 ? (
          categories.map((cat) => (
            <div
              key={cat._id}
              className="bg-white rounded-2xl shadow-sm hover:shadow-md transition overflow-hidden border"
            >
              {cat.images && (
                <img
                  src={cat.images}
                  alt={cat.name}
                  className="h-40 w-full object-cover"
                />
              )}

              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-800">{cat.name}</h3>
                    <p className="text-xs text-gray-500">{cat.slug}</p>
                  </div>

                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                    Active
                  </span>
                </div>

                <div className="mt-3 text-sm">
                  <p className="text-gray-500">Parent</p>
                  <p className="font-medium">
                    {cat.parent?.name || "Root Category"}
                  </p>
                </div>

                <div className="mt-3">
                  <p className="text-gray-500 text-sm mb-1">Children</p>

                  <div className="flex flex-wrap gap-2">
                    {cat.children?.length ? (
                      cat.children.map((child) => (
                        <span
                          key={child._id}
                          className="text-xs bg-gray-100 px-2 py-1 rounded"
                        >
                          {child.name}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-400">No Children</span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(cat)}
                    className="flex-1 text-sm border rounded-lg py-1 hover:bg-gray-100"
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => handleDelete(cat._id)}
                    className="flex-1 text-sm border border-red-500 text-red-500 rounded-lg py-1 hover:bg-red-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center text-gray-500 py-10">
            No Categories Found
          </div>
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-xl rounded-2xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                {editingId ? "Edit Category" : "Add Category"}
              </h2>

              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-black"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Category name"
                className="w-full border rounded-lg p-2"
              />

              <input
                value={formData.slug}
                readOnly
                className="w-full border rounded-lg p-2 bg-gray-100"
              />

              <CategorySelector
                onChange={handleCategoryChange}
                categories={categories}
              />

              <input
                type="file"
                name="images"
                onChange={handleChange}
                className="w-full"
              />

              {preview && (
                <img
                  src={preview}
                  className="h-40 w-full object-cover rounded-lg"
                />
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-black text-white py-2 rounded-lg hover:bg-gray-800"
              >
                {loading
                  ? "Saving..."
                  : editingId
                    ? "Update Category"
                    : "Create Category"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
